/*
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2026 Pearson Education, Inc.
 * All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

import axios from 'axios';
import env from '../../../../common/env';
import utils from '../../../../common/utils';
import runGraphService from '../../../studio/services/graphs/RunGraphService';
import parseRunGraphStreamToText from '../../../studio/vibeflow/services/parseRunGraphStreamToText';
import MAVEN from './mavenConfig';
import { addStep, readStreamPayload } from './mavenParse';

/**
 * Runs the Maven graph for one question, the same way Studio's Test Graph does: runGraphService
 * (which sends useStream: true) and the shared Studio stream reader. Maven only adds progress steps.
 * There is no thread: the last few turns go in `history`, so Maven can follow up on "yes, go ahead".
 *
 * @param {{ question: string, pageContext?: object, history?: Array<{ question: string, answer: string }>,
 *   onProgress?: (p: { text: string, steps: string[] }) => void,
 *   signal?: AbortSignal }} params
 * @returns {Promise<{ answer: string, requestId: string, steps: string[] }>}
 */
export const askQuestion = async ({ question, pageContext, history, onProgress, signal }) => {
  // Our own request id: berlin-api logs this run under it, and verify reads those logs.
  const questionId = utils.uuid();
  const payload = {
    requestId: questionId,
    input: question,
    bookId: MAVEN.bookId,
    ...(pageContext ? { pageContext } : {}),
    ...(history?.length ? { history } : {})
  };
  const response = await runGraphService({
    graphId: MAVEN.graphId,
    graphVersion: MAVEN.version,
    tenantId: MAVEN.tenantId,
    payload,
    signal
  });

  let steps = [];
  let failure = null;
  let shownText = '';
  const answer = await parseRunGraphStreamToText(response, 'The graph returned an empty answer.', {
    allowEmpty: true,
    onEvent: (event, textSoFar) => {
      const progress = readStreamPayload(event);
      if (progress?.error) failure = progress.error;
      if (progress?.step) steps = addStep(steps, progress.step);
      if (progress?.step || textSoFar !== shownText) {
        shownText = textSoFar;
        onProgress?.({ text: textSoFar, steps });
      }
    }
  });

  if (failure) throw new Error(failure);
  if (!answer.trim()) throw new Error('The graph returned an empty answer.');
  return { answer, requestId: questionId, steps };
};

/**
 * Asks ai-admin-services to check the answer: did its SQL really run, do its proof ids exist,
 * are its numbers in the results? It reads what the question ran from berlin-api's logs, by request id.
 * @param {string} answer
 * @param {string} requestId - the id askQuestion sent with the question
 * @returns {Promise<{ status: string, sql: object[], requestIds: object[], numbers: object, warnings: string[] }>}
 */
export const verifyAnswer = async (answer, requestId) => {
  const { data } = await axios.post(
    `${env.AI_ADMIN_API_BASE_URL}/maven/verify`,
    { answer, requestId },
    { headers: { Authorization: `Bearer ${utils.getToken()}`, 'Content-Type': 'application/json' } }
  );
  return data;
};
